export const moduleState = {
  fundingRequests: [], // Array of all pending funding requests
  fundingRequest: null, // Current request being shown
  loading: false,
  error: null,
  checkingForRequests: false,
};

export const getters = {
  fundingRequests: state => state.fundingRequests,
  fundingRequest: state => state.fundingRequest,
  loading: state => state.loading,
  error: state => state.error,
  checkingForRequests: state => state.checkingForRequests,
  hasPendingRequest: state => !!state.fundingRequest,
  hasPendingRequests: state => state.fundingRequests.length > 0,
  pendingRequestsCount: state => state.fundingRequests.length,
};

export const mutations = {
  SET_FUNDING_REQUESTS(state, requests) {
    state.fundingRequests = requests || [];
    // Set the first request as current if we have any
    state.fundingRequest = requests && requests.length > 0 ? requests[0] : null;
  },
  REMOVE_CURRENT_REQUEST(state) {
    if (!state.fundingRequest) {
      return;
    }

    // Find and remove the current request from the array
    const index = state.fundingRequests.findIndex(
      req =>
        req.fundingManagerClientId === state.fundingRequest.fundingManagerClientId &&
        req.expiresDatetimeUtc === state.fundingRequest.expiresDatetimeUtc
    );

    if (index !== -1) {
      state.fundingRequests.splice(index, 1);
    }

    // Set the next request as current (first item in remaining array), or null if no more
    state.fundingRequest = state.fundingRequests?.[0] ?? null;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  SET_CHECKING_FOR_REQUESTS(state, checking) {
    state.checkingForRequests = checking;
  },
  CLEAR_FUNDING_REQUEST(state) {
    state.fundingRequest = null;
    state.fundingRequests = [];
    state.error = null;
  },
};

export const actions = {
  /**
   * Check for pending funding requests for the current user
   */
  async checkForPendingFundingRequests({ commit, dispatch, rootState }) {
    commit('SET_CHECKING_FOR_REQUESTS', true);
    commit('CLEAR_ERROR');

    const clientId = rootState.client?.client?.id;
    if (!clientId) {
      commit('SET_ERROR', 'Client ID not available');
      commit('SET_CHECKING_FOR_REQUESTS', false);
      return [];
    }

    const response = await dispatch(
      'request/get',
      {
        url: `/api/bulkfunding/recipient-requests/${clientId}`,
        errorMessage: 'Failed to check for pending funding requests',
        loadingMutation: 'fundingRequest/SET_CHECKING_FOR_REQUESTS',
      },
      { root: true }
    );

    if (!response) {
      // Error already handled by request module
      commit('SET_CHECKING_FOR_REQUESTS', false);
      return [];
    }

    // Transform all pending requests to match expected format
    if (response.data && response.data.length > 0) {
      const transformedRequests = response.data.map(pendingRequest => ({
        fundingManagerClientId: pendingRequest.fundingManagerClientId,
        fundingRecipientClientId: clientId,
        managerName: pendingRequest.fundingManagerName,
        expiresDatetimeUtc: pendingRequest.fundingRequestExpirationUtc,
      }));

      // Use SET_FUNDING_REQUESTS to store all requests and set first as current
      commit('SET_FUNDING_REQUESTS', transformedRequests);
    } else {
      commit('SET_FUNDING_REQUESTS', []);
    }

    commit('SET_CHECKING_FOR_REQUESTS', false);
    return response.data;
  },

  /**
   * Accept a pending funding request
   */
  async acceptFundingRequest({ commit, dispatch, state, rootState }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    const clientId = rootState.client?.client?.id;
    if (!clientId) {
      commit('SET_ERROR', 'Client ID not available');
      commit('SET_LOADING', false);
      return { success: false, error: 'Client ID not available' };
    }

    if (!state.fundingRequest) {
      commit('SET_ERROR', 'No funding request to accept');
      commit('SET_LOADING', false);
      return { success: false, error: 'No funding request to accept' };
    }

    const response = await dispatch(
      'request/post',
      {
        url: `/api/bulkfunding/recipient/${clientId}/accept`,
        data: {
          fundingManagerClientId: state.fundingRequest.fundingManagerClientId,
        },
        errorMessage: 'Failed to accept funding request',
        successMessage: 'Funding request accepted successfully',
        loadingMutation: 'fundingRequest/SET_LOADING',
      },
      { root: true }
    );

    if (!response) {
      // Error already handled by request module (toast shown)
      commit('SET_LOADING', false);
      return { success: false };
    }

    // Remove current request and move to next one
    commit('REMOVE_CURRENT_REQUEST');
    commit('SET_LOADING', false);

    return { success: true, data: response.data };
  },

  /**
   * Decline/reject a funding request
   */
  async declineFundingRequest({ commit, dispatch, state, rootState }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    const clientId = rootState.client?.client?.id;
    if (!clientId) {
      commit('SET_ERROR', 'Client ID not available');
      commit('SET_LOADING', false);
      return { success: false, error: 'Client ID not available' };
    }

    if (!state.fundingRequest) {
      commit('SET_ERROR', 'No funding request to decline');
      commit('SET_LOADING', false);
      return { success: false, error: 'No funding request to decline' };
    }

    const response = await dispatch(
      'request/post',
      {
        url: `/api/bulkfunding/recipient/${clientId}/reject`,
        data: {
          fundingManagerClientId: state.fundingRequest.fundingManagerClientId,
        },
        errorMessage: 'Failed to reject funding request',
        successMessage: 'Funding request rejected',
        loadingMutation: 'fundingRequest/SET_LOADING',
      },
      { root: true }
    );

    if (!response) {
      // Error already handled by request module (toast shown)
      commit('SET_LOADING', false);
      return { success: false };
    }

    // Remove current request and move to next one
    commit('REMOVE_CURRENT_REQUEST');
    commit('SET_LOADING', false);

    return { success: true, data: response.data };
  },

  /**
   * Clear any errors
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  /**
   * Clear all funding request data
   */
  clearFundingRequestData({ commit }) {
    commit('CLEAR_FUNDING_REQUEST');
  },
};
